import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Banner,
  Button,
  EmptyState,
  Modal,
  Row,
  Skeleton,
  Tag,
  Toast
} from '../components/primitives';
import {
  type BulkAction,
  type CellValue
} from '../components/Table';
import {
  useDocumentsListVM,
  type DocumentsListInitialOverrides
} from '../lib/vm/useDocumentsListVM';
import type {
  DocumentLifecycle,
  DocumentRow,
  DocumentsTab
} from '../lib/models/documents';
import {
  getDocumentsView,
  setDocumentsView,
  resolveVisibleProperties
} from '../lib/viewPreferences';
import { UNTITLED_DOCUMENT_TITLE } from '../lib/editor/defaultTitles';
import { EditorCover, type EditorCoverTarget } from '../components/EditorCover';
import {
  ListControlBar,
  ViewTabs,
  type FacetDef,
  type GroupByOption,
  type PropertyChoice,
  type FilterDict
} from '../components/list-controls';
import {
  DocumentsGallery,
  GALLERY_CHIP_KEYS,
  GALLERY_CHIP_LABELS,
  GALLERY_DEFAULT_CHIPS,
  type GalleryChipKey
} from '../components/documents/DocumentsGallery';
import {
  DocumentsTable,
  TABLE_COLUMN_KEYS,
  TABLE_COLUMN_LABELS,
  TABLE_DEFAULT_VISIBLE,
  TABLE_LOCKED_COLUMNS
} from '../components/documents/DocumentsTable';
import './Documents.css';

/**
 * `/documents` — list page styled to match `/work-items`.
 *
 * Composes the shared list-controls primitives over `useDocumentsListVM`:
 * view tabs (Table | Gallery), a control bar (Search · + Filter · active
 * chips · Group by · Properties), and either the `<Table>` primitive or
 * the card grid `<DocumentsGallery>` depending on `state.tab`. The bulk-
 * action bar lives on both views via shared selection state.
 */

// ── Table columns and Gallery chips ────────────────────────────────────
const LIFECYCLE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' }
];

const COLS_STORAGE_KEY_TABLE = 'penrose:documents:cols:table';
const COLS_STORAGE_KEY_GALLERY = 'penrose:documents:cols:gallery';

// ── View tabs / Group by options ───────────────────────────────────────
const TAB_OPTIONS = [
  { key: 'gallery', label: 'Gallery' },
  { key: 'table', label: 'Table' }
] as const;

const DOC_GROUP_BY_OPTIONS: ReadonlyArray<GroupByOption> = [
  { key: 'none', label: 'None' },
  { key: 'type', label: 'Type' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'owner', label: 'Owner' }
];

/**
 * Valid lifecycle option keys, used to validate the `lifecycle` deep-link
 * param before seeding the VM. Mirrors `DocumentLifecycle`; unknown values are
 * dropped so a bad share-link falls back to the default (unfiltered) view.
 */
const VALID_LIFECYCLE: ReadonlySet<DocumentLifecycle> = new Set([
  'draft',
  'published',
  'archived'
]);

export function Documents() {
  // Read the `lifecycle` deep-link param via the router-aware
  // `useSearchParams()` — under the app's `HashRouter` the query lives in the
  // URL fragment (`#/documents?lifecycle=archived`) and this reads it from
  // there. The agent archive banner deep-links here; seeding the VM's
  // lifecycle filter from the param makes the archived view actually show on
  // load. When the param is absent (or invalid) we pass no override, so
  // existing in-page filter behavior is unchanged.
  const [searchParams] = useSearchParams();
  const initialOverrides = useMemo(() => {
    const lifecycle = searchParams
      .getAll('lifecycle')
      .flatMap((v) => v.split(',').map((s) => s.trim()))
      .filter((v): v is DocumentLifecycle =>
        (VALID_LIFECYCLE as ReadonlySet<string>).has(v)
      );
    // Resolve the initial view: an explicit `?tab=` wins (share / deep links),
    // then the locally-stored preference, then Gallery (the first-time default
    // now that Gallery leads the tab strip). The VM's own `window.location`
    // read is empty under HashRouter, so this router-aware seam owns the seed.
    const rawTab = searchParams.get('tab');
    const tab: DocumentsTab =
      rawTab === 'gallery' || rawTab === 'table'
        ? rawTab
        : (getDocumentsView() ?? 'gallery');
    const overrides: DocumentsListInitialOverrides = { tab };
    if (lifecycle.length > 0) overrides.lifecycle = lifecycle;
    return overrides;
    // Computed once for the initial VM seed; the VM owns URL state thereafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vm = useDocumentsListVM({ overrides: initialOverrides });
  const [creating, setCreating] = useState(false);
  const [createErrorMsg, setCreateErrorMsg] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  // Inline-edit failure toast. Single-message queue is enough for the
  // user-facing rate (one click per second at most); subsequent toasts
  // replace the prior message.
  const [toast, setToast] = useState<string | null>(null);
  // The slide-up editor cover. Opening a doc keeps this list mounted (scroll +
  // filters preserved) instead of navigating to the editor route.
  const [cover, setCover] = useState<EditorCoverTarget | null>(null);
  const openDoc = useCallback((id: string) => setCover({ kind: 'document', id }), []);

  // Preserve test harness for URL-binding specs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    (window as unknown as { __docsHarness: unknown }).__docsHarness = {
      state: vm.state,
      actions: vm.actions,
      status: vm.status,
      derived: vm.derived,
      total: vm.derived.total,
      pageSize: vm.derived.pageSize,
      rowCount: vm.derived.rows.length,
      loading: vm.derived.loading,
      error: vm.derived.error
    };
  }, [vm]);

  const loading = vm.derived.loading || vm.status === 'loading';
  const errored = vm.status === 'error' || vm.derived.error !== null;
  const unauth = vm.status === 'unauth';
  const noOrg = vm.status === 'empty';

  const rows = vm.derived.rows;
  const hasFilters = vm.derived.hasActiveFilters;
  const showFilteredEmpty =
    !loading && !errored && rows.length === 0 && hasFilters;
  const showZeroDocsEmpty =
    !loading &&
    !errored &&
    rows.length === 0 &&
    !hasFilters &&
    vm.status === 'ready';

  const countLabel = `${vm.derived.total} ${vm.derived.total === 1 ? 'document' : 'documents'}`;

  const pageSize = vm.derived.pageSize;
  const total = vm.derived.total;
  const page = vm.state.page;
  const pageCount = total > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const rangeStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = total > 0 ? Math.min(page * pageSize, total) : 0;
  const showFooter =
    !loading && !errored && !unauth && !noOrg && total > 0;
  const prevDisabled = page <= 1;
  const nextDisabled = page * pageSize >= total;

  // ── Lookup maps for resolving id → label in chips/dropdowns ──────────
  const typeLookup = useMemo(
    () =>
      new Map(vm.derived.typeOptions.map((t) => [t.id, t.name] as const)),
    [vm.derived.typeOptions]
  );
  const ownerLookup = useMemo(
    () =>
      new Map(
        vm.derived.memberOptions.map(
          (m) => [m.userId, m.displayName] as const
        )
      ),
    [vm.derived.memberOptions]
  );
  // ── Facets for the + Filter popover ──────────────────────────────────
  const facets = useMemo<ReadonlyArray<FacetDef>>(
    () => [
      {
        key: 'type',
        label: 'Type',
        options: vm.derived.typeOptions.map((t) => ({
          id: t.id,
          label: t.name
        }))
      },
      {
        key: 'lifecycle',
        label: 'Lifecycle',
        options: LIFECYCLE_OPTIONS.map((o) => ({
          id: o.value,
          label: o.label
        }))
      },
      {
        key: 'owner',
        label: 'Owner',
        options: vm.derived.memberOptions.map((m) => ({
          id: m.userId,
          label: m.displayName || m.userId
        }))
      }
    ],
    [vm.derived, vm.state.type]
  );

  // ── Filters dict for ListControlBar (FilterPopover + ActiveFilterChips) ─
  const filters: FilterDict = useMemo(
    () => ({
      type: [...vm.state.type],
      lifecycle: [...vm.state.lifecycle],
      owner: [...vm.state.owner]
    }),
    [vm.state]
  );

  const onFiltersChange = useCallback(
    (next: FilterDict) => {
      vm.actions.setFilter('type', next.type ?? []);
      vm.actions.setFilter('lifecycle', next.lifecycle ?? []);
      vm.actions.setFilter('owner', next.owner ?? []);
    },
    [vm.actions]
  );

  // ── Visible properties — per-tab default + URL/localStorage fallback ─
  const visibleProperties = useMemo<ReadonlyArray<string>>(() => {
    const isGallery = vm.state.tab === 'gallery';
    const storedCols =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(
            isGallery ? COLS_STORAGE_KEY_GALLERY : COLS_STORAGE_KEY_TABLE
          )
        : null;
    // Validate against THIS surface's key universe so foreign keys leaked via
    // the shared `?cols=` URL param can't strand the gallery/table (see
    // resolveVisibleProperties).
    return resolveVisibleProperties(
      vm.state.cols,
      storedCols,
      isGallery ? GALLERY_CHIP_KEYS : TABLE_COLUMN_KEYS,
      isGallery ? GALLERY_DEFAULT_CHIPS : TABLE_DEFAULT_VISIBLE
    );
  }, [vm.state.cols, vm.state.tab]);

  const setVisibleProperties = useCallback(
    (next: ReadonlyArray<string>) => {
      vm.actions.setCols(next);
      if (typeof window !== 'undefined') {
        const key =
          vm.state.tab === 'gallery'
            ? COLS_STORAGE_KEY_GALLERY
            : COLS_STORAGE_KEY_TABLE;
        window.localStorage.setItem(key, next.join(','));
      }
    },
    [vm.actions, vm.state.tab]
  );

  const propertyChoices: ReadonlyArray<PropertyChoice> = useMemo(() => {
    if (vm.state.tab === 'gallery') {
      return GALLERY_CHIP_KEYS.map((k: GalleryChipKey) => ({
        key: k,
        label: GALLERY_CHIP_LABELS[k]
      }));
    }
    return TABLE_COLUMN_KEYS.map((k) => ({
      key: k,
      label: TABLE_COLUMN_LABELS[k]
    }));
  }, [vm.state.tab]);

  const propertyLockedKeys =
    vm.state.tab === 'table' ? TABLE_LOCKED_COLUMNS : [];

  const handleCellEdit = useCallback(
    async (rowId: string, columnKey: string, newValue: CellValue) => {
      let result: { ok: boolean; error?: string } | null = null;
      let fieldLabel = columnKey;
      try {
        if (columnKey === 'lifecycle' && typeof newValue === 'string') {
          fieldLabel = 'Lifecycle';
          result = await vm.actions.setLifecycle(
            rowId,
            newValue as DocumentLifecycle
          );
        } else if (columnKey === 'type') {
          fieldLabel = 'Type';
          // Type is required (NOT NULL) — ignore an empty selection rather than
          // attempting to clear it.
          if (typeof newValue === 'string' && newValue.length > 0) {
            result = await vm.actions.setDocumentType(rowId, newValue);
          }
        } else if (columnKey === 'owner') {
          fieldLabel = 'Owner';
          const next =
            typeof newValue === 'string' && newValue.length > 0
              ? newValue
              : null;
          result = await vm.actions.setOwner(rowId, next);
        }
      } catch (err) {
        result = { ok: false, error: String(err) };
      }
      if (result && !result.ok) {
        setToast(`Couldn't save ${fieldLabel} — try again`);
      }
    },
    [vm.actions]
  );

  const handleBulkAction = useCallback(
    async (rowIds: string[], action: BulkAction) => {
      if (action.id === 'archive' && rowIds.length > 0) {
        await vm.actions.archiveDocuments(rowIds);
        setSelectedIds(new Set());
      }
    },
    [vm.actions]
  );

  // ── Gallery selection toggle ─────────────────────────────────────────
  const onGalleryToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const archiveSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    await vm.actions.archiveDocuments(ids);
    setSelectedIds(new Set());
  }, [selectedIds, vm.actions]);

  // ── Clear-filters handler for the empty state ────────────────────────
  function clearFilters() {
    vm.actions.setFilter('type', []);
    vm.actions.setFilter('lifecycle', []);
    vm.actions.setFilter('owner', []);
    vm.actions.setFilter('created_after', null);
    vm.actions.setFilter('created_before', null);
    vm.actions.setFilter('updated_after', null);
    vm.actions.setFilter('updated_before', null);
    vm.actions.setSearch('');
  }

  return (
    <section
      className="doc-page"
      data-testid="documents-page"
      data-status={vm.status}
      data-active-org-id={vm.activeOrgId ?? ''}
      data-row-count={rows.length}
      data-total={vm.derived.total}
      data-loading={loading ? '1' : '0'}
      data-page={vm.state.page}
      data-sort={
        vm.state.sort.direction === 'desc'
          ? `-${vm.state.sort.columnKey}`
          : vm.state.sort.columnKey
      }
      data-q={vm.state.q}
      data-tab={vm.state.tab}
      data-group-by={vm.state.groupBy}
      data-lifecycle={vm.state.lifecycle.join(',')}
      data-type={vm.state.type.join(',')}
      data-owner={vm.state.owner.join(',')}
    >
      <header className="doc-header">
        <div className="doc-header-title">
          <h1 className="doc-h1">Documents</h1>
          <Tag data-testid="documents-count">{countLabel}</Tag>
        </div>
        <Button
          variant="primary"
          size="sm"
          disabled={!vm.derived.canCreate || creating}
          data-testid="documents-new-btn"
          onClick={async () => {
            if (creating) return;
            setCreating(true);
            setCreateErrorMsg(null);
            const firstTypeId = vm.derived.typeOptions[0]?.id;
            const result = await vm.actions.createDocument({
              title: UNTITLED_DOCUMENT_TITLE,
              typeId: firstTypeId
            });
            setCreating(false);
            if (result.ok) {
              openDoc(result.id);
            } else {
              setCreateErrorMsg(
                "Couldn't create document. Please try again."
              );
            }
          }}
        >
          + New document
        </Button>
      </header>

      <ViewTabs
        options={TAB_OPTIONS.map((t) => ({ key: t.key, label: t.label }))}
        current={vm.state.tab}
        onChange={(next) => {
          vm.actions.setTab(next as DocumentsTab);
          setDocumentsView(next as DocumentsTab);
          setSelectedIds(new Set());
        }}
        ariaLabel="Documents views"
        testIdPrefix="documents-route"
      />

      <ListControlBar
        testIdPrefix="documents"
        activeTab={vm.state.tab}
        searchQuery={vm.state.q}
        onSearchChange={vm.actions.setSearch}
        searchPlaceholder="Search documents…"
        searchAriaLabel="Search documents"
        filters={filters}
        onFiltersChange={onFiltersChange}
        facets={facets}
        groupByOptions={DOC_GROUP_BY_OPTIONS}
        groupBy={vm.state.groupBy}
        onGroupByChange={(next) =>
          vm.actions.setGroupBy(next as typeof vm.state.groupBy)
        }
        propertiesMode={vm.state.tab === 'table' ? 'columns' : 'chips'}
        propertyChoices={propertyChoices}
        propertyLockedKeys={propertyLockedKeys}
        visibleProperties={visibleProperties}
        onVisiblePropertiesChange={setVisibleProperties}
      />

      {errored ? (
        <Banner kind="error" title="Couldn't load documents">
          {vm.derived.error ?? 'Refresh the page or try again.'}
        </Banner>
      ) : unauth ? (
        <Banner kind="default" title="Sign in to view documents">
          Your session expired. Sign in to continue.
        </Banner>
      ) : noOrg ? (
        <Banner kind="default" title="No active org">
          Join or create an org to start writing documents.
        </Banner>
      ) : loading && rows.length === 0 ? (
        <div className="doc-skeleton" data-testid="documents-skeleton">
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
        </div>
      ) : showFilteredEmpty ? (
        <EmptyState
          data-testid="documents-empty-filtered"
          action={
            <Button
              variant="default"
              size="sm"
              onClick={clearFilters}
              data-testid="documents-clear-filters"
            >
              Clear filters
            </Button>
          }
        >
          No documents match your filters.
        </EmptyState>
      ) : showZeroDocsEmpty ? (
        <EmptyState data-testid="documents-empty-zero">
          No documents yet.
        </EmptyState>
      ) : vm.state.tab === 'gallery' ? (
        <DocumentsGallery
          rows={rows as ReadonlyArray<DocumentRow>}
          visibleChips={visibleProperties}
          groupBy={vm.state.groupBy}
          selectedIds={selectedIds}
          onToggleSelect={onGalleryToggleSelect}
          onRowOpen={openDoc}
          typeLookup={typeLookup}
          ownerLookup={ownerLookup}
          systemValuesByDoc={vm.derived.systemValuesByDoc}
        />
      ) : (
        <DocumentsTable
          rows={rows as ReadonlyArray<DocumentRow>}
          systemValuesByDoc={vm.derived.systemValuesByDoc}
          typeOptions={vm.derived.typeOptions}
          memberOptions={vm.derived.memberOptions}
          typeLookup={typeLookup}
          ownerLookup={ownerLookup}
          visibleProperties={visibleProperties}
          sort={vm.state.sort}
          onSortChange={vm.actions.setSort}
          onColsChange={setVisibleProperties}
          onRowOpen={openDoc}
          onCellEdit={handleCellEdit}
          onBulkAction={handleBulkAction}
          dataTestId="documents"
        />
      )}

      {/* Gallery has its own bulk-action bar; Table primitive ships one
          internally. When in Gallery + something is selected, render a
          minimal action bar so users can archive. */}
      {vm.state.tab === 'gallery' && selectedIds.size > 0 ? (
        <Row
          template="auto 1fr auto"
          className="doc-bulkbar"
          data-testid="documents-gallery-bulkbar"
        >
          <span className="doc-bulkbar-count">
            {selectedIds.size} selected
          </span>
          <span />
          <span className="doc-bulkbar-actions">
            <Button
              variant="default"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              data-testid="documents-gallery-bulk-clear"
            >
              Clear
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={archiveSelected}
              data-testid="documents-gallery-bulk-archive"
            >
              Archive
            </Button>
          </span>
        </Row>
      ) : null}

      {showFooter ? (
        <Row
          template="1fr auto"
          className="doc-footer"
          data-testid="documents-footer"
          data-page={page}
          data-page-count={pageCount}
        >
          <span className="doc-footer-range" data-testid="documents-footer-range">
            Showing {rangeStart}–{rangeEnd} of {total}
          </span>
          <span className="doc-footer-nav">
            <Button
              variant="ghost"
              size="sm"
              disabled={prevDisabled}
              onClick={() => vm.actions.setPage(page - 1)}
              data-testid="documents-footer-prev"
            >
              Prev
            </Button>
            <span className="doc-footer-page" data-testid="documents-footer-page">
              Page {page} of {pageCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={nextDisabled}
              onClick={() => vm.actions.setPage(page + 1)}
              data-testid="documents-footer-next"
            >
              Next
            </Button>
          </span>
        </Row>
      ) : null}

      {/* No type-picker modal on this page yet — the + New document button
          creates a draft with the org's first type and routes to the editor.
          A modal lands when the org has zero types or many. */}
      <Modal
        open={false}
        onClose={() => undefined}
        title="Pick a document type"
        dataTestId="documents-new-type-picker"
      >
        <div />
      </Modal>

      {createErrorMsg ? (
        <Toast
          title="Couldn't create document"
          onDismiss={() => setCreateErrorMsg(null)}
        >
          {createErrorMsg}
        </Toast>
      ) : null}

      {toast ? (
        <Toast onDismiss={() => setToast(null)}>{toast}</Toast>
      ) : null}

      <EditorCover
        target={cover}
        onNavigate={setCover}
        onClose={() => {
          setCover(null);
          // Refresh titles / newly-created rows in place (no remount → scroll kept).
          vm.actions.reload();
        }}
      />
    </section>
  );
}
